<template>
  <div class="crop-stage-program">
    <div class="crop-stage-program__heading-wrapper">
      <h2 class="crop-stage-program__heading">
        <slot></slot>
      </h2>
      <!-- organic farming switch -->
      <organic-farming-switch v-model="organic" v-if="hasOrganicProducts" />
      <!-- navigation -->
      <div class="crop-stage-program__navigation">
        <button
          class="navigation-button navigation-button--prev"
          ref="navigationBack"></button>
        <button
          class="navigation-button navigation-button--next"
          ref="navigationNext"></button>
      </div>
    </div>
    <div class="crop-stage-program__content">
      <!-- swiper top -->
      <div class="crop-stage-program__swiper-top swiper" ref="swiperTop">
        <div class="swiper-wrapper">
          <div
            v-for="(crop, idx) in displayedProducts"
            :key="crop.id"
            class="swiper-slide"
            :class="{
              'swiper-slide--active': idx === 0,
            }">
            <crop-stage-program-thumb :item="crop" />
          </div>
        </div>
      </div>

      <!-- swiper bottom -->
      <div class="crop-stage-program__swiper-bottom swiper" ref="swiperBottom">
        <div class="crop-stage-program__products-title">
          {{ t.productsTitle }}
        </div>
        <div class="swiper-wrapper">
          <div
            v-for="slide in displayedProducts"
            :key="slide.id"
            class="swiper-slide">
            <div
              v-for="(product, key) in groupByApplication(
                organic ? slide.products_organic : slide.products,
              )"
              :key="product.id"
              class="crop-stage-program__product-application-group">
              <!-- products -->
              <crop-stage-products :products="product">
                <!-- group name -->
                <div class="crop-stage-program__product-application">
                  {{ key }}
                </div>
              </crop-stage-products>
            </div>
          </div>
        </div>
      </div>

      <!-- pdf link -->
      <div class="crop-stage-program__pdf">
        <p>{{ t.pdfText }}</p>
        <a
          :href="`${lang}print/pdf/node/${cspId}`"
          class="button button--primary"
          @click="({ target }) => target.setAttribute('disabled', true)">
          {{ t.pdfLinkText }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
// 3rd
import Swiper, {
  Controller,
  EffectFade,
  Navigation,
  Thumbs,
  Virtual,
} from 'swiper';
// components
import CropStageProducts from './CropStageProducts';
import CropStageProgramThumb from './CropStageProgramThumb';
import OrganicFarmingSwitch from '../products/OrganicFarmingSwitch';
// utils
import translationStrings from '../../translations/translationStrings';

export default {
  name: 'crop-stage-program',
  components: {
    CropStageProducts,
    CropStageProgramThumb,
    OrganicFarmingSwitch,
  },
  props: {
    program: {
      type: Array,
      required: true,
    },
    cspId: {
      type: Number,
      required: true,
    },
    lang: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      organic: false,
      organicId: 83, // Organic Farming
      hasOrganicProducts: false,
      swiperTop: null,
      swiperBottom: null,
      sliderSlideActive: 'swiper-slide--active',
      t: translationStrings,
    };
  },
  computed: {
    products() {
      return this.program.filter((p) => {
        return p.products.length > 0;
      });
    },
    productsOrganic() {
      return this.program.filter((p) => {
        return p.products_organic.length > 0;
      });
    },
    displayedProducts() {
      return this.organic ? this.productsOrganic : this.products;
    },
  },
  watch: {
    organic() {
      if (this.swiperTop) this.swiperTop.destroy();
      if (this.swiperBottom) this.swiperBottom.destroy();

      this.iniSwiper();
      this.resetActive();
    },
  },
  mounted() {
    this.iniSwiper();

    // check if program has any Organic products
    this.hasOrganicProducts = this.program.some(
      (p) => p.products_organic.length > 0,
    );
  },
  methods: {
    groupByApplication(products) {
      return products.reduce((acc, value) => {
        if (!acc[value.application]) {
          acc[value.application] = [];
        }
        acc[value.application].push(value);

        return acc;
      }, {});
    },
    resetActive() {
      this.swiperTop.slides.forEach((s) => {
        s.classList.add(this.sliderSlideActive);
        if (!s.classList.contains('swiper-slide-thumb-active')) {
          s.classList.remove(this.sliderSlideActive);
        }
      });
    },
    iniSwiper() {
      const options = {
        centeredSlides: true,
        preventInteractionOnTransition: true,
        slideToClickedSlide: true,
        observer: true,
        observeParents: true,
      };
      const linkToRight = 'swiper-slide--to-right';
      const linkToLeft = 'swiper-slide--to-left';

      // thumbs
      this.swiperTop = new Swiper(this.$refs.swiperTop, {
        ...options,
        breakpoints: {
          701: {
            centeredSlides: false,
            slidesPerView: 3.55,
          },
        },
        grabCursor: true,
        slidesOffsetAfter: -1,
        slidesOffsetBefore: -1,
        slidesPerView: 1.55,
        watchSlidesProgress: true,
        watchSlidesVisibility: true,
      });

      // slides
      this.swiperBottom = new Swiper(this.$refs.swiperBottom, {
        ...options,
        allowTouchMove: false,
        autoHeight: true,
        controller: {
          control: this.swiperTop,
          by: 'container',
        },
        effect: 'fade',
        fadeEffect: {
          crossFade: true,
        },
        modules: [Controller, EffectFade, Navigation, Thumbs, Virtual],
        navigation: {
          prevEl: this.$refs.navigationBack,
          nextEl: this.$refs.navigationNext,
        },
        slideActiveClass: 'swiper-slide--active',
        speed: 400,
        thumbs: {
          multipleActiveThumbs: false,
          swiper: this.swiperTop,
        },
        virtualTranslate: true,
        on: {
          slideChangeTransitionStart: (s) => {
            // add top green line transition
            const { activeIndex } = s;
            const prevIndex = s.previousIndex ?? 0;
            const { slides } = this.swiperTop;
            const activeSlide = slides[activeIndex].classList.contains(
              'swiper-slide-thumb-active',
            );

            if (prevIndex < activeIndex) {
              slides[prevIndex].classList.add(linkToLeft);
              slides[prevIndex].classList.remove(this.sliderSlideActive);
              slides[activeIndex].classList.add(linkToRight);

              if (activeSlide) {
                slides[activeIndex].classList.add(this.sliderSlideActive);
              }
            } else if (prevIndex > activeIndex) {
              slides[prevIndex].classList.add(linkToRight);
              slides[prevIndex].classList.remove(this.sliderSlideActive);
              slides[activeIndex].classList.add(linkToLeft);

              if (activeSlide) {
                slides[activeIndex].classList.add(this.sliderSlideActive);
              }
            }
          },
          transitionEnd: () => {
            // remove top green line transition
            this.swiperTop.slides.forEach((s) => {
              s.classList.remove(linkToLeft, linkToRight);
            });
          },
        },
      });
    },
  },
};
</script>
